import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Calendar, Edit, Trash2, MapPin, Clock, User, Loader, Loader2 } from "lucide-react";
import { constants } from "../../../constants";
import axios from "axios";
import AddEventForm from "./AddEventForm";

const EventList = ({ onEditEvent }) => {
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showAddEventForm, setShowAddEventForm] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loader, setLoader] = useState(null);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(
        `${constants.serverURL}/api/events/listEvents`
      );
      setEvents(response.data.events || []);
      setIsLoading(false);
    } catch (err) {
      console.error("Failed to fetch events:", err);
      setError(err.message);
      setIsLoading(false);
    }
  };

  const handleDeleteEvent = async () => {
    setLoader("delete");
    const eventId = selectedEvent._id;
    try {
      const token = localStorage.getItem("adminToken");
      await axios.delete(
        `${constants.serverURL}/api/events/deleteEvent/${eventId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      // Remove event from local state
      setEvents(events.filter(event => event._id !== eventId));
      setDeleteConfirm(null);
    } catch (err) {
      console.error("Failed to delete event:", err);
      setError(err.response?.data?.message || "Failed to delete event");
    }finally{
      setLoader(null);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const addEvent = (newEvent) => {
    setEvents([newEvent, ...events]);
  };

  const updateEvent = (updatedEvent) => {
    setEvents(events.map(event => 
      event._id === updatedEvent._id ? updatedEvent : event
    ));
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
        Error: {error}
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* <div className="flex justify-end p-4">
        <button
          onClick={() => setShowAddEventForm(true)}
          className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition"
        >
          Create Event
        </button>
      </div> */}
      {!showAddEventForm && (
      <div
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
       
      >
        {events.map((event) => (
          <article
            key={event._id}
            className="bg-white rounded-lg shadow-md overflow-hidden"
          >
            <div className="relative bg-white p-[12px] rounded-xl shadow-sm border border-gray-100 flex items-center justify-center overflow-hidden">
              <img
                src={`${constants.serverURL}${event.imgUrl}`}
                alt={event.title}
                className="w-full h-auto object-contain rounded-md"
              />
              <div className="absolute top-2 right-2 flex space-x-2">
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={() => onEditEvent(event)}
                  className="bg-blue-500 text-white p-2 rounded-full hover:bg-blue-600 transition"
                >
                  <Edit size={16} />
                </motion.button>
                <motion.button
                  
                whileTap={{ scale: 0.95 }}
                  onClick={() => {setDeleteConfirm(event._id);setSelectedEvent(event)}}
                  className="bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition"
                >
                  <Trash2 size={16} />
                </motion.button>
              </div>
            </div>
            
            <div className="p-4">
              <h2 className="text-xl font-bold text-gray-800 mb-2">
                {event.title}
              </h2>
              
              {event.presentedBy && (
                <div className="flex items-center text-sm text-gray-600 mb-2">
                  <User size={14} className="mr-1" />
                  <span>{event.presentedBy}</span>
                </div>
              )}
              
              <div className="flex items-center text-sm text-gray-600 mb-2">
                <Calendar size={14} className="mr-1" />
                <span>{event.date}</span>
              </div>
              
              {event.time && (
                <div className="flex items-center text-sm text-gray-600 mb-2">
                  <Clock size={14} className="mr-1" />
                  <span>{event.time}</span>
                </div>
              )}
              
              {event.location && (
                <div className="flex items-center text-sm text-gray-600 mb-3">
                  <MapPin size={14} className="mr-1" />
                  <span>{event.location}</span>
                </div>
              )}
              
              <div 
                className="text-gray-600 mb-4 line-clamp-3 text-sm"
                dangerouslySetInnerHTML={{ __html: event.description }}
              />
              
              {event.topics && event.topics.length > 0 && (
                <div className="mb-3">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Features: {event.topics.length}
                  </span>
                </div>
              )}
              
              {event.locations && event.locations.length > 0 && (
                <div className="mb-3">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Cities: {event.locations.length}
                  </span>
                </div>
              )}
              
              <div className="flex items-center text-sm text-gray-500">
                <Calendar size={14} className="mr-1" />
                <span>Created: {new Date(event.createdAt).toLocaleDateString()}</span>
              </div>
            </div>
          </article>
        ))}
      </div>
    )}
      {events.length === 0 && !showAddEventForm && (
        <div className="text-center py-12">
          <Calendar size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No events found</h3>
          <p className="text-gray-500">Create your first event to get started.</p>
          <button
            onClick={() => setShowAddEventForm(true)}
            className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition"
          >
            Create Event
          </button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm!==null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg p-6 max-w-md w-full mx-4"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Delete Event
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this event? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteEvent()}
                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition"
              >
                {loader == "delete" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : "Delete"}
                
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {showAddEventForm && (
        <AddEventForm addEvent={addEvent} cancel={() => setShowAddEventForm(false)} />
      )}
    </div>
  );
};

export default EventList;